import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../App";
import { Link, Outlet, useParams } from "react-router-dom";
import debounce from "lodash.debounce";

const SMSPage = () => {
    const [inbox, setInbox] = useState([]);
    const [refreshInbox, setRefreshInbox] = useState(0);
    const [filteredInbox, setFilteredInbox] = useState([]);
    const [inboxSearch, setInboxSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(inboxSearch);
    const [hasMore, setHasMore] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const [unread, setUnread] = useState(false);
    const [dir, setDir] = useState("desc");
    const [page, setPage] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const params = useParams();

    const [hasError, setHasError] = useState(null);

    const inboxRef = useRef(null);

    const { request, emitter } = useContext(AppContext);

    document.title = "Staff | SMS";

    emitter.on("message", () => {
        setRefreshInbox((prev) => prev + 1);
    });

    useEffect(() => {
        const el = inboxRef.current;

        if (!el) return;

        const handleScroll = () => {
            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;

            if (nearBottom && hasMore && !pageLoading) {
                setPageLoading(true);
                setPage((prev) => prev + 1);
            }
        };

        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, [hasMore, pageLoading]);

    useEffect(() => {
        const fetchInbox = async () => {
            try {
                let params = `?dir=${dir}&page=${page}`;
                if (debouncedSearch) {
                    params += `&query=${debouncedSearch.replace("+", "%2B")}`;
                }
                if (unread) {
                    params += "&read=false";
                }
                const response = await request({
                    url: `/staff/sms/inbox${params}`,
                    method: "GET",
                });

                const { texts } = response;

                if (debouncedSearch) {
                    if (page === 1) {
                        setFilteredInbox(texts);
                    } else {
                        setFilteredInbox((prev) => [...prev, ...texts]);
                    }
                } else {
                    if (page === 1) {
                        setFilteredInbox(texts);
                        setInbox(texts);
                    } else {
                        setFilteredInbox((prev) => [...prev, ...texts]);
                        setInbox((prev) => [...prev, ...texts]);
                    }
                }

                setHasMore(texts.length > 0);
                setPageLoading(false);
            } catch (err) {
                console.log(err);
                setHasError(err);
            }
        };

        fetchInbox();
    }, [request, refreshInbox, debouncedSearch, unread, dir, page]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(inboxSearch);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [inboxSearch]);

    useEffect(() => {
        if (hasError) {
            throw hasError;
        }
    }, [hasError]);

    const handleInputChange = (event, setState) => {
        setState(event.target.value);
    };

    const extractDate = (dateTimeString) => {
        const date = new Date(dateTimeString);
        const now = new Date();

        const isCurrentYear = date.getFullYear() === now.getFullYear();

        const options = isCurrentYear ? { month: "long", day: "numeric" } : { year: "numeric", month: "numeric", day: "numeric" };

        return date.toLocaleDateString("en-US", options);
    };

    const determineBadgeContent = (user) => {
        if (
            user.freeze_member &&
            ["frozen_selected", "frozen_signed", "frozen_screening", "frozen_first_cycling", "frozen_second_cycling", "frozen_cycle_complete"].includes(user.freeze_member.cycle_first_status)
        ) {
            return (
                <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.00031 0.733334C5.33168 0.733334 5.60031 1.00196 5.60031 1.33333V1.76251L5.66531 1.73001C5.9617 1.58182 6.3221 1.70195 6.4703 1.99834C6.61849 2.29473 6.49836 2.65513 6.20197 2.80332L5.60031 3.10415V4.01905L6.41869 4.48825L7.20809 4.03248L7.16778 3.36102C7.14792 3.03025 7.39997 2.746 7.73074 2.72615C8.06152 2.70629 8.34576 2.95834 8.36562 3.28911L8.36997 3.36164L8.74164 3.14706C9.02861 2.98137 9.39557 3.07969 9.56126 3.36666C9.72695 3.65363 9.62862 4.02059 9.34165 4.18628L8.96999 4.40086L9.03062 4.44089C9.30715 4.62347 9.38332 4.99565 9.20074 5.27219C9.01816 5.54872 8.64598 5.62489 8.36944 5.44231L7.80811 5.0717L7.01578 5.52916L7.01851 6.47241L7.80811 6.9283L8.36944 6.55769C8.64598 6.37511 9.01816 6.45128 9.20074 6.72781C9.38332 7.00435 9.30715 7.37653 9.03062 7.55911L8.96999 7.59914L9.34166 7.81372C9.62863 7.97941 9.72695 8.34637 9.56126 8.63334C9.39557 8.92031 9.02862 9.01863 8.74164 8.85295L8.36998 8.63836L8.36562 8.71089C8.34576 9.04167 8.06152 9.29371 7.73074 9.27385C7.39997 9.254 7.14792 8.96975 7.16778 8.63898L7.20809 7.96752L6.4158 7.51008L5.60031 7.9841V8.89585L6.20197 9.19668C6.49836 9.34487 6.61849 9.70527 6.4703 10.0017C6.3221 10.298 5.9617 10.4182 5.66531 10.27L5.60031 10.2375V10.6667C5.60031 10.998 5.33168 11.2667 5.00031 11.2667C4.66894 11.2667 4.40031 10.998 4.40031 10.6667V10.2375L4.3353 10.27C4.03892 10.4182 3.67851 10.298 3.53032 10.0017C3.38212 9.70527 3.50226 9.34487 3.79865 9.19668L4.40031 8.89585V7.98095L3.58194 7.51175L2.79253 7.96753L2.83284 8.63898C2.8527 8.96975 2.60065 9.254 2.26987 9.27385C1.9391 9.29371 1.65485 9.04167 1.635 8.71089L1.63064 8.63837L1.25899 8.85295C0.97202 9.01863 0.605065 8.92031 0.439375 8.63334C0.273686 8.34637 0.372007 7.97941 0.65898 7.81372L1.03063 7.59914L0.969997 7.55911C0.693462 7.37653 0.617295 7.00435 0.799873 6.72781C0.982452 6.45128 1.35464 6.37511 1.63117 6.55769L2.19252 6.92831L2.98485 6.47084L2.98212 5.52759L2.19252 5.07169L1.63117 5.44231C1.35464 5.62489 0.982452 5.54872 0.799873 5.27219C0.617295 4.99565 0.693462 4.62347 0.969997 4.44089L1.03063 4.40086L0.65898 4.18628C0.372007 4.02059 0.273686 3.65363 0.439375 3.36666C0.605065 3.07969 0.97202 2.98137 1.25899 3.14706L1.63064 3.36163L1.635 3.28911C1.65485 2.95834 1.9391 2.70629 2.26987 2.72615C2.60065 2.746 2.8527 3.03025 2.83284 3.36102L2.79253 4.03247L3.58484 4.48992L4.40031 4.01592V3.10415L3.79865 2.80332C3.50226 2.65513 3.38212 2.29473 3.53032 1.99834C3.67851 1.70195 4.03892 1.58182 4.3353 1.73001L4.40031 1.76251V1.33333C4.40031 1.00196 4.66894 0.733334 5.00031 0.733334ZM4.99759 5.05673L4.18214 5.53073L4.18487 6.47419L5.00303 6.94328L5.81849 6.46927L5.81577 5.52581L4.99759 5.05673Z"
                        fill="#393939"
                    />
                </svg>
            );
        } else if (user.freeze_member && ["not_a_candidate_for_frozen", "frozen_declined", "frozen_disqualified"].includes(user.freeze_member.cycle_first_status)) {
            return (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.09444 2.03842C3.11973 1.14901 4.68319 0.666656 6.86016 0.666656H8.73336C9.06473 0.666656 9.33336 0.935286 9.33336 1.26666V2.19999C9.33336 2.21146 9.33303 2.22292 9.33237 2.23437C9.21079 4.35289 8.62366 5.9164 7.59169 6.94877C6.5539 7.98696 5.15484 8.39999 3.60002 8.39999H2.47699C2.4121 8.67486 2.3633 8.96363 2.32968 9.26625C2.29308 9.59559 1.99643 9.83292 1.66709 9.79632C1.33774 9.75973 1.10042 9.46308 1.13702 9.13373C1.18835 8.67177 1.27215 8.22476 1.39443 7.79673C0.662419 6.83537 0.660156 5.91384 0.660156 5.46666L0.660159 5.46498C0.663764 4.17721 1.05446 2.94059 2.09444 2.03842ZM2.90939 7.19999C2.98719 7.04793 3.07261 6.90192 3.16591 6.76198C3.62188 6.07802 4.28726 5.50669 5.2437 5.08161C5.54651 4.94703 5.68288 4.59245 5.5483 4.28964C5.41372 3.98683 5.05914 3.85045 4.75633 3.98504C3.61277 4.49329 2.76148 5.20529 2.16745 6.09634C2.1031 6.19287 2.04201 6.29112 1.9841 6.39101C1.86014 6.02309 1.86015 5.70171 1.86016 5.47156L1.86016 5.46753C1.86324 4.42233 2.17257 3.55924 2.88077 2.94489C3.60385 2.31764 4.83712 1.86666 6.86016 1.86666H8.13336V2.18258C8.01952 4.11988 7.49174 5.35137 6.74299 6.10041C5.99788 6.84582 4.94694 7.19999 3.60002 7.19999H2.90939Z"
                        fill="#393939"
                    />
                </svg>
            );
        }

        return <span>{user.freeze_member ? "SM" : "IP"}</span>;
    };

    return (
        <div className="flex flex-col h-screen">
            <div className="w-full h-[88px] flex-shrink-0 flex border-b border-white-200 items-center">
                <div className="flex items-center ml-8 w-1/3">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12.0002 9.66669V9.68002M6.66683 9.66669V9.68002M17.3335 9.66669V9.68002M1.3335 23V5.66669C1.3335 4.60582 1.75492 3.58841 2.50507 2.83826C3.25521 2.08811 4.27263 1.66669 5.3335 1.66669H18.6668C19.7277 1.66669 20.7451 2.08811 21.4953 2.83826C22.2454 3.58841 22.6668 4.60582 22.6668 5.66669V13.6667C22.6668 14.7276 22.2454 15.745 21.4953 16.4951C20.7451 17.2453 19.7277 17.6667 18.6668 17.6667H6.66683L1.3335 23Z"
                            stroke="#6B6B6B"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h2 className="mb-0 ml-2">SMS</h2>
                </div>
                <div className="relative w-full max-w-sm">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                        </svg>
                    </span>
                    <input
                        value={inboxSearch}
                        onChange={(e) => handleInputChange(e, setInboxSearch)}
                        type="text"
                        placeholder="Search by name, donor code, or phone number"
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                    />
                    {inboxSearch.length ? (
                        <span className="absolute inset-y-5 right-0 flex items-center">
                            <div
                                onClick={() => {
                                    setInboxSearch("");
                                    setDebouncedSearch("");
                                    setPage(1);
                                }}
                                className="mr-4 w-8 h-6 font-body font-normal text-sm text-freeze-secondary-500"
                            >
                                Clear
                            </div>
                        </span>
                    ) : null}
                </div>
            </div>

            <div className="flex-grow flex overflow-hidden">
                <div className="relative flex flex-col w-1/3 border-r border-white-200">
                    <div className="sticky top-0 z-10 border-b border-white-200 bg-white flex items-center justify-between px-4 pt-6">
                        <div className="flex items-center">
                            <div
                                onClick={() => setUnread(false)}
                                className={`flex justify-between items-center w-20 h-10  py-2 px-3 ${!unread ? "border-b-2 border-black text-black" : "text-black-600"}`}
                            >
                                <div className="font-body font-bold text-xs">ALL</div>
                                {inbox.filter((t) => !t.read).length ? (
                                    <div
                                        className={`flex-shrink-0 w-[23px] h-[23px] ${
                                            !unread ? "bg-black text-white" : "bg-white-200 text-black"
                                        } rounded-full flex items-center justify-center text-xs`}
                                    >
                                        {inbox.filter((t) => !t.read).length}
                                    </div>
                                ) : null}
                            </div>
                            <div
                                onClick={() => setUnread(true)}
                                className={`flex justify-between items-center w-28 h-10  py-2 px-3 ${unread ? "border-b-2 border-black text-black" : "text-black-600"}`}
                            >
                                <div className="font-body font-bold text-xs">UNREAD</div>
                                {inbox.filter((t) => !t.read || (t.unread && t.unread > t.read)).length ? (
                                    <div
                                        className={`flex-shrink-0 w-[23px] h-[23px] ${
                                            unread ? "bg-black text-white" : "bg-white-200 text-black"
                                        } rounded-full flex items-center justify-center text-xs`}
                                    >
                                        {inbox.filter((t) => !t.read || (t.unread && t.unread > t.read)).length}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div
                            onClick={() => {
                                if (dir === "desc") {
                                    setDir("asc");
                                } else {
                                    setDir("desc");
                                }
                                setPage(1);
                                if (inboxRef.current) {
                                    inboxRef.current.scrollTop = 0;
                                }
                            }}
                            className={`flex-shrink-0 w-[32px] h-[32px] rounded-full border border-white-200 flex items-center justify-center text-xs`}
                        >
                            <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M12.1668 1.5V16.5M12.1668 1.5L14.6668 4M12.1668 1.5L9.66683 4M6.3335 14L3.8335 16.5M3.8335 16.5L1.3335 14M3.8335 16.5V1.5"
                                    stroke="#6B6B6B"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                    <div className="w-full flex-grow overflow-y-auto" ref={inboxRef}>
                        {filteredInbox.map((text, i) => {
                            if (!selectedUser && params.userId === text.user.id) {
                                setSelectedUser(text.user);
                            }

                            return (
                                <Link
                                    key={text.id}
                                    to={`/staff/sms/${text.user.id}`}
                                    onClick={() => {
                                        setSelectedUser(text.user);
                                    }}
                                    className={`cursor-pointer grid grid-cols-8 h-20 px-5 border-b border-b-white-200 items-center ${
                                        selectedUser && text.user.id === selectedUser.id ? "bg-[#FCFCFC] border-l-4 border-l-black" : "bg-white"
                                    }`}
                                >
                                    {text.user.user_photos ? (
                                        <div className={`col-span-1 flex justify-center items-center`}>
                                            <div className={`relative w-10 h-10 rounded-full px-2 py-1 text-black text-xl mr-2`} style={{ backgroundColor: "#D2E9E6" }}>
                                                <img src={text.user.user_photos[0].cdn_url} alt={text.user.user_photos[0].name} className="absolute inset-0 w-full h-full object-cover rounded-full" />

                                                <div
                                                    className={`absolute -bottom-1 -right-1 font-bold text-[7px] font-body border border-white-100 ${
                                                        text.user.freeze_member ? "bg-primary text-secondary" : "bg-family-apricot text-black"
                                                    } rounded-full w-4 h-4 flex items-center justify-center`}
                                                >
                                                    {determineBadgeContent(text.user)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`col-span-1 flex justify-center items-center`}>
                                            <div className="relative flex justify-center items-center mr-2 px-2 py-1 col-span-2 w-10 h-10 rounded-full bg-secondary text-white">
                                                <div>{text.user.firstName ? text.user.firstName.charAt(0).toUpperCase() : "?"}</div>
                                                <div
                                                    className={`absolute -bottom-1 -right-1 font-bold text-[7px] font-body border border-white-100 ${
                                                        text.user.freeze_member ? "bg-primary text-secondary" : "bg-family-apricot text-black"
                                                    } rounded-full w-4 h-4 flex items-center justify-center`}
                                                >
                                                    {determineBadgeContent(text.user)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-span-7">
                                        <div className="flex items-center justify-between pb-1">
                                            <div className="flex items-center font-bold font-body text-base">
                                                <div>
                                                    {text.user.donorCode ? `[${text.user.donorCode}]` : null} {text.user.firstName} {text.user.lastName}
                                                </div>
                                            </div>
                                            <div className="font-normal font-body text-xs text-[#939393] items-center">
                                                <div className="text-xs">{extractDate(text.created_at)}</div>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between gap-2">
                                            <div className={`font-body text-sm ${!text.read ? "font-medium text-black" : "font-normal text-black-600"} truncate`} style={{ minWidth: 0 }}>
                                                {text.photo_id ? "[image] " : ""}
                                                {text.body}
                                            </div>

                                            {!text.read && <div className="flex-shrink-0 w-[23px] h-[23px] bg-black text-white rounded-full flex items-center justify-center text-xs">1</div>}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
                <div className="w-2/3 bg-gray-100">
                    {selectedUser ? (
                        <Outlet />
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center">
                            <img alt="Cofertility logo" src={`${process.env.PUBLIC_URL}/images/inbox_empty.svg`} />
                            <h3 className="w-1/2 font-display font-normal text-2xl whitespace-pre-line text-center mt-6 mb-2">No SMS selected</h3>
                            <div className="w-1/2 font-body font-light text-sm text-center">Select a member's thread to read and reply to texts</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SMSPage;
